import type { ReactNode } from "react";
import { CarbonsixIcon, ElevatedSurface, Spinner, Text } from "@carbon6/c6-frontend-design";
import { appTextModes } from "../theme";
import styles from "./AuthCard.module.css";

// 카드 그림자만 DS elevation 기본값보다 또렷하게 override — c6 의 떠 있는 면 그림자를 쓴다
// (elevation 2 의 `0 3px 6px` 보다 크고, 다크에서 α 가 따로 올라간다). styled-components
// (ElevatedSurface) 의 box-shadow 를 확실히 이기려면 클래스가 아닌 inline style 로 준다
// (!important 회피).
const CARD_SHADOW = "var(--c6-elevation-shadow-overlay)";

/** 로그인/콜백 흐름의 인라인 상태 한 줄. 진행중은 스피너, 실패는 danger 텍스트. */
export type AuthCardStatus =
  { kind: "idle" } | { kind: "pending"; message: string } | { kind: "failed"; message: string };

type AuthCardProps = {
  titleId?: string;
  /** 부트 스플래시처럼 타이틀 없이 상태만 보여줄 땐 생략. */
  title?: ReactNode;
  subtitle?: ReactNode;
  status?: AuthCardStatus;
  /** 카드 하단 액션(로그인 버튼, 돌아가기 버튼 등). */
  children?: ReactNode;
};

/**
 * 인증 흐름 공용 카드. 로그인(`LandingPage`)과 OAuth 콜백(`AuthCallbackPage`)이
 * 같은 카드·상태줄을 쓰도록 셸을 한곳에 둔다. 콜백은 별도 라우트(`/auth/callback`)로
 * 남지만 화면은 로그인 카드와 동일하게 이어지게 보인다.
 */
export function AuthCard({ titleId, title, subtitle, status, children }: AuthCardProps) {
  const activeStatus = status && status.kind !== "idle" ? status : null;

  return (
    <main className={styles.page}>
      <ElevatedSurface
        as="section"
        elevation={2}
        hoverElevation={false}
        className={styles.panel}
        style={{ boxShadow: CARD_SHADOW }}
        aria-labelledby={titleId}
      >
        <CarbonsixIcon size={48} className={styles.brandIcon} />
        {title || subtitle ? (
          <div className={styles.header}>
            {title ? (
              <Text variant={appTextModes["title-bold"]} id={titleId} className={styles.title}>
                {title}
              </Text>
            ) : null}
            {subtitle ? (
              <Text variant={appTextModes["caption-normal"]} className={styles.subtitle}>
                {subtitle}
              </Text>
            ) : null}
          </div>
        ) : null}
        {/* 상태 슬롯은 status prop 이 있으면 idle 이어도 항상 자리를 잡아둔다.
            메시지가 떠도 카드 높이·버튼 위치가 바뀌지 않게 한다. */}
        {status ? (
          <div className={styles.status} aria-live="polite">
            {activeStatus?.kind === "pending" ? (
              <span className={styles.statusSpinner}>
                <Spinner size="md" />
              </span>
            ) : null}
            {activeStatus ? (
              <Text
                variant={appTextModes["caption-normal"]}
                className={
                  activeStatus.kind === "failed" ? styles.statusError : styles.statusMessage
                }
              >
                {activeStatus.message}
              </Text>
            ) : null}
          </div>
        ) : null}
        {children}
      </ElevatedSurface>
    </main>
  );
}
